import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import { formatRole } from '../utils/roleLabel';
import './SMEStyles.css';

const FileIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const UsersIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const SMEDashboard = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        papersToReview: 0,
        setsToSelect: 0,
        examiners: 0
    });

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            const [papersRes, setsRes, examinersRes] = await Promise.all([
                API.get('/sub-questions/sme/papers-with-reviews').catch(() => ({ data: { papers: [] } })),
                API.get('/sme-selection/pending-requests').catch(() => ({ data: { requests: [] } })),
                API.get('/users/my-examiners').catch(() => ({ data: { examiners: [] } })),
            ]);

            const papers = papersRes.data.papers || [];
            const requests = setsRes.data.requests || [];
            const examiners = Array.isArray(examinersRes.data)
                ? examinersRes.data
                : (examinersRes.data?.examiners ?? []);

            setStats({
                papersToReview: papers.length,
                setsToSelect: requests.length,
                examiners: examiners.length
            });
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="sme-page">
                <div className="sme-spinner-wrap">
                    <div className="sme-spinner" />
                </div>
            </div>
        );
    }

    const statCards = [
        { icon: <FileIcon />, colorClass: 'sme-stat-icon--blue', value: stats.papersToReview, label: 'Papers to Review', path: '/sme-papers' },
        // { icon: <LayersIcon />, colorClass: 'sme-stat-icon--purple', value: stats.setsToSelect, label: 'Sets to Select', path: '/sme-selection' },
        { icon: <UsersIcon />, colorClass: 'sme-stat-icon--green', value: stats.examiners, label: 'My Examiners', path: '/my-examiners' },
    ];

    const quickActions = [
        { icon: <FileIcon />, colorClass: 'sme-stat-icon--blue', label: 'Review Papers', desc: 'Review variations submitted by examiners', path: '/sme-papers' },
        // { icon: <LayersIcon />, colorClass: 'sme-stat-icon--purple', label: 'Select Question Sets', desc: 'Pick the best paper sets from generated options', path: '/sme-selection' },
        // { icon: <ChecklistIcon />, colorClass: 'sme-stat-icon--amber', label: 'Variation Reviews', desc: 'Approve or reject pending question variations', path: '/sme/variation-reviews' },
        { icon: <UsersIcon />, colorClass: 'sme-stat-icon--green', label: 'My Examiners', desc: 'Manage examiners in your subject', path: '/my-examiners' },
    ];

    return (
        <div className="sme-page">
            <div className="sme-page-header">
                <div>
                    <h1>Subject Matter Expert Dashboard</h1>
                    <p>Welcome back, {user?.name} ({formatRole(user?.role)})</p>
                    <p>Review papers, select question sets, and manage your examiners</p>
                </div>
            </div>

            <div className="sme-stats-grid">
                {statCards.map(card => (
                    <div
                        key={card.label}
                        className="sme-stat-card sme-stat-card--clickable"
                        onClick={() => navigate(card.path)}
                    >
                        <div className={`sme-stat-icon ${card.colorClass}`}>
                            {card.icon}
                        </div>
                        <div>
                            <div className="sme-stat-value">{card.value}</div>
                            <div className="sme-stat-label">{card.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Paper sets awaiting selection */}
            {stats.setsToSelect > 0 && (
                <div className="sme-card sme-card--accent-info">
                    <div className="sme-card-header">
                        <div>
                            <h2>Paper Sets Awaiting Selection</h2>
                            <p>Each request contains question paper sets that need your selection.</p>
                        </div>
                        <span className="sme-badge sme-badge--info">{stats.setsToSelect} Pending</span>
                    </div>
                    <div className="sme-card-body">
                        <p style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>
                            You have {stats.setsToSelect} generation request{stats.setsToSelect !== 1 ? 's' : ''} waiting for your review.
                        </p>
                        <button className="sme-btn sme-btn--primary" onClick={() => navigate('/sme-selection')}>
                            Review &amp; Select Paper Sets
                        </button>
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <div className="sme-card">
                <div className="sme-card-header">
                    <h2>Quick Actions</h2>
                </div>
                <div className="sme-card-body">
                    <div className="sme-actions-grid">
                        {quickActions.map(action => (
                            <div
                                key={action.path}
                                className="sme-action-card"
                                onClick={() => navigate(action.path)}
                            >
                                <div className={`sme-action-icon ${action.colorClass}`}>
                                    {action.icon}
                                </div>
                                <div>
                                    <div className="sme-action-title">{action.label}</div>
                                    <div className="sme-action-desc">{action.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default SMEDashboard;
